import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Scene, User, ImageFile, ProcessingStatus } from '../types';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/logo.gif';
import Dashboard from './Dashboard';
import SceneEditor from './sceneEditor';
import ShareModal from './ShareModal';
import SceneDetailsPanel from './SceneDetailsPanel';
import DownloadModal from './DownloadModal';
import Toast from './Toast';
import ReconstructionFeedback from './ReconstructionFeedback';
import { customerApi } from '../services/customerApi';
import { UploadResult } from '../types/upload.types';
import { Scan, ScanStatus } from '../types/scan.types';
import { ReconstructionImageData } from '../services/s3MetadataReader.service';
import { Viewer } from '@ydrive/gs-viewer';
import { useLocation } from 'react-router-dom';
import { useUploadCtx } from '../contexts/UploadContext';

interface Props {
  user: User | null;
  onLogout: () => void;
}

export default function SceneManager({ user, onLogout }: Props) {
  const { user: authUser, authenticated, role } = useAuth();
  const location = useLocation();
  const { reset: resetUuid } = useUploadCtx();
  // Scene and scan state
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [scans, setScans] = useState<Scan[]>([]);
  const [isLoadingScenes, setIsLoadingScenes] = useState(false);
  const [scenesError, setScenesError] = useState<string | null>(null);
  const [selectedScene, setSelectedScene] = useState<Scene | null>(null);
  const [sceneLoadingStates, setSceneLoadingStates] = useState<Record<string, boolean>>({});
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  //run reconstruction monitoring only once on page load
  const [hasRunReconstructionMonitoring, setHasRunReconstructionMonitoring] = useState(false);
  
  // Image handling state
  const [images, setImages] = useState<ImageFile[]>([]);
  const [selectedImage, setSelectedImage] = useState<ImageFile | null>(null);
  const [sceneFileSizes, setSceneFileSizes] = useState<Record<string, number>>({});

  
  // Processing state
  const [processing, setProcessing] = useState<ProcessingStatus>({
    status: 'pending',
    progress: 0
  });
  
  // UI state management
  const [currentView, setCurrentView] = useState<'dashboard' | 'editor' | '3d-viewer'>('dashboard');
  const [showFeedback, setShowFeedback] = useState(false);
  
  // 3D Viewer state
  const [selectedSceneFor3D, setSelectedSceneFor3D] = useState<Scene | undefined>(undefined);
  const [modelLoading, setModelLoading] = useState(false);
  const modelContainerRef = useRef<HTMLDivElement>(null);
  const progressTextRef = useRef<HTMLParagraphElement>(null);
  const viewerRef = useRef<any>(null);
  
  // Sharing state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [sceneToShare, setSceneToShare] = useState<Scene | null>(null);
  
  // Download modal state
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [sceneToDownload, setSceneToDownload] = useState<Scene | null>(null);

  // status check reconstruction
const isCheckingStatus = useRef(false);
  
  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false
  });


  // Handle image selection and updates
  const handleImagesSelected = useCallback(async (newImages: ImageFile[]) => {
    setImages(prevImages => {
      const existingIds = new Set(prevImages.map(img => img.id));
      const newImagesFiltered = newImages.filter(img => !existingIds.has(img.id));
      const updatedImages = [...prevImages, ...newImagesFiltered];
      
      // Update scene if one is selected
      if (selectedScene) {
        setScenes(prevScenes => prevScenes.map(scene =>
          scene.id === selectedScene.id
            ? {
                ...scene,
                images: updatedImages,
                quality: {
                  ...scene.quality,
                  imageCount: updatedImages.length,
                  overall: 0 // Will be calculated when real processing is implemented
                }
              }
            : scene
        ));
      }
      return updatedImages;
    });
  }, [selectedScene]);

  // Handle image click for details view
  const handleImageClick = (image: ImageFile) => {
    setSelectedImage(image);
  };

  // Handle image deletion
  const handleImageDelete = (imageId: string) => {
    setImages(prevImages => {
      const updatedImages = prevImages.filter(img => img.id !== imageId);
      
      // Update scene if one is selected
      if (selectedScene) {
        setScenes(prevScenes => prevScenes.map(scene =>
          scene.id === selectedScene.id
            ? {
                ...scene,
                images: updatedImages,
                quality: {
                  ...scene.quality,
                  imageCount: updatedImages.length
                }
              }
            : scene
        ));
      }
      return updatedImages;
    });
    setSelectedImage(null);
  };

  // Real reconstruction function that triggers backend processing
  const handleReconstruction = async (
    scene: Scene, 
    sequences: ReconstructionImageData[][], 
    uploadResult?: UploadResult
  ) => {
    if (!uploadResult || !uploadResult.sessionUUID) {
      console.error('[SceneManager] Cannot trigger reconstruction: missing upload result or sessionUUID');
      setProcessing({
        status: 'error',
        progress: 0,
        message: 'Missing upload data for reconstruction'
      });
      return;
    }
  
    try {
      console.log('[SceneManager] Starting reconstruction for scene:', scene.name);
      console.log('[SceneManager] Session UUID:', uploadResult.sessionUUID);
      console.log('[SceneManager] Upload Result:', uploadResult);
      console.log('[SceneManager] Sequences received:', {
        sequenceCount: sequences.length,
        totalImages: sequences.reduce((sum, seq) => sum + seq.length, 0),
        imagesPerSequence: sequences.map(seq => seq.length)
      });
  
      // Progress step 1: Preparing
      setProcessing({
        status: 'processing',
        progress: 10,
        message: 'Preparing reconstruction request...'
      });
  
      // Simulate brief processing time for UX
      await new Promise(resolve => setTimeout(resolve, 300));
  
      // Progress step 2: Validating sequences
      setProcessing({
        status: 'processing',
        progress: 30,
        message: 'Validating image sequences...'
      });
  
      // Validate the sequences we received
      const totalImages = sequences.reduce((sum, seq) => sum + seq.length, 0);
      console.log(`[SceneManager] Validated sequences:`, {
        sequenceCount: sequences.length,
        totalImages: totalImages,
        imagesPerSequence: sequences.map(seq => seq.length)
      });
  
      // Another brief pause for UX
      await new Promise(resolve => setTimeout(resolve, 300));
  
      // Progress step 3: Building reconstruction data
      setProcessing({
        status: 'processing',
        progress: 60,
        message: 'Preparing reconstruction data...'
      });
  
      const reconstructionData = {
        sequence_set_uuid: uploadResult.sessionUUID,
        sequences: sequences,  // ✅ Use passed sequences (already read from S3 by sceneEditor)
        preset: scene.capturePreset,
      };
  
      console.log('[SceneManager] Triggering reconstruction with sequences:', reconstructionData);
      
      // Progress step 4: Triggering reconstruction
      const response = await customerApi.triggerReconstruction(reconstructionData);
      
      console.log('[SceneManager] Reconstruction triggered successfully:', response);
      
      // Progress step 5: Complete
      setProcessing({
        status: 'completed',
        progress: 100,
        message: 'Reconstruction triggered successfully! Processing will begin shortly.'
      });
  
      // Update scene status to processing
      setScenes(prevScenes => 
        prevScenes.map(s => 
          s.id === scene.id 
            ? { ...s, status: 'processing' as const, updatedAt: Date.now() }
            : s
        )
      );
  
      showToast('Reconstruction started successfully!', 'success');
  
    } catch (error) {
      console.error('[SceneManager] Failed to trigger reconstruction:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      setProcessing({
        status: 'error',
        progress: 0,
        message: `Reconstruction failed: ${errorMessage}`
      });
  
      showToast(`Reconstruction failed: ${errorMessage}`, 'error');
    }
  };
  // Show toast notification
  const showToast = (message: string, type: 'success' | 'error' | 'info') => {
    setToast({
      message,
      type,
      isVisible: true
    });
  };

  // Handle toast close
  const handleToastClose = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // Get 3D model URL for a scene
const getSceneModelUrl = async (scene: Scene): Promise<{ modelUrl: string; helperFiles?: any[] } | null> => {
  try {
    setLoadingMessage('Checking 3D model availability...');
    console.log(`[SceneManager] Getting 3D model URL for scene: ${scene.name}, status: ${scene.status}`);
    
    // Only attempt to get model URL for completed scenes
    if (scene.status !== 'completed') {
      console.log(`[SceneManager] Scene not completed, no 3D model available`);
      return null;
    }

    setLoadingMessage('Retrieving 3D model from server...');
    
    // Call the Gaussian splat API endpoint with error handling
    console.log(`[SceneManager] Calling Gaussian splat API for scan ID: ${scene.id}`);
    
    try {
      const response = await customerApi.getGaussianSplatObject(scene.id);

      console.log(`[SceneManager] API Response:`, response);

      if (response.success && response.data?.gaussianFile?.signedUrl) {
        const gaussianUrl = response.data.gaussianFile.signedUrl;
        const fileSize = response.data.gaussianFile.size;
        const helperFiles = response.data.helperFiles || [];
        
        console.log(`[SceneManager] Successfully retrieved Gaussian URL:`, {
          fileName: response.data.gaussianFile.fileName,
          fileSize: fileSize,
          helperFilesCount: helperFiles.length,
          scanId: response.data.scanId,
          url: gaussianUrl.substring(0, 100) + '...' // Log partial URL for debugging
        });

        // Log helper files details for debugging
        if (helperFiles.length > 0) {
          console.log(`[SceneManager] Helper files found:`, helperFiles.map(f => ({
            fileName: f.fileName,
            fileType: f.fileType,
            size: f.size
          })));
        }

        // Store file size immediately from API response
        if (fileSize !== undefined) {
          setSceneFileSizes(prev => ({ ...prev, [scene.id]: fileSize }));
          console.log(`[SceneManager] File size from API for ${scene.name}: ${fileSize} bytes`);
        }

        // Validate URL before returning
        try {
          const urlObj = new URL(gaussianUrl);
          console.log(`[SceneManager] URL validation passed: ${urlObj.hostname}`);
          setLoadingMessage('3D model retrieved successfully!');
          return { 
            modelUrl: gaussianUrl, 
            helperFiles: helperFiles.length > 0 ? helperFiles : undefined 
          };
        } catch (urlError) {
          console.error(`[SceneManager] Invalid URL format:`, urlError);
          return null;
        }
      } else {
        console.log(`[SceneManager] No Gaussian file found in response:`, response);
        return null;
      }
    } catch (apiError) {
      console.error(`[SceneManager] API call failed:`, apiError);
      showToast('Failed to retrieve 3D model data', 'error');
      return null;
    }
    
  } catch (error) {
    console.error('[SceneManager] Error getting scene model URL:', error);
    showToast('Failed to retrieve 3D model URL', 'error');
    return null;
  }
};


// Transform Scan to Scene format
const transformScanToScene = useCallback((scan: Scan): Scene => {
  return {
    id: scan.scanId,
    name: scan.title,
    description: scan.description || '',
    type: (scan.category as Scene['type']) || 'unknown',
    cameraType: 'smartphone',
    captureType: 'single',
    createdAt: new Date(scan.createdAt).getTime(),
    updatedAt: new Date(scan.updatedAt).getTime(),
    status: scan.status === ScanStatus.Completed ? 'completed' : 
            scan.status === ScanStatus.Processing ? 'processing' : 
            scan.status === ScanStatus.Failed ? 'error' : 
            scan.status === ScanStatus.Uploaded ? 'uploaded' :
            scan.status === ScanStatus.TempSaved ? 'pending' : 'pending',
    images: [],
    thumbnailUrl: scan.thumbnailUrl,
    s3FolderPath: scan.s3FolderPath,
    versions: [],
    quality: {
      overall: 0,
      imageCount: scan.inputImageCount || 0,
      feedback: scan.subStatus ? {
        rating: { accuracy: 0, completeness: 0, textureQuality: 0, performance: 0 },
        issues: [],
        comment: scan.subStatus,
        submittedAt: Date.now()
      } : undefined
    },
    capturePreset: ['drive', 'indoor', 'object', 'outdoor_walk', 'drone', 'mix_drone', 'quick'].includes(scan.category || '') 
  ? (scan.category as Scene['capturePreset'])
  : undefined,
    fileSize: scan.fileSize,
    creatorId: scan.creatorId,
    // Store the gaussian model path for 3D viewing
    ...(scan.gaussModelPath && { gaussModelPath: scan.gaussModelPath })
  };
}, []);

  // Load user's scans based on role and userId
  const loadUserScans = useCallback(async () => {
    if (!authUser || !authenticated) {
      setScenes([]);
      setScans([]);
      return;
    }

    setIsLoadingScenes(true);
    setScenesError(null);

    try {
      console.log(`[SceneManager] Loading scans for user: ${authUser.uid}, role: ${role}`);
      
      // Determine which scans to load based on role
      let scansData: Scan[];
      
      switch (role) {
        case 'admin':
          // Admins can see all scans and need owner usernames
          const [usersData, allScans] = await Promise.all([
            customerApi.getAllUsers(),
            customerApi.getScans({})
          ]);

          // Build map userId -> username (email prefix)
          const userNameMap = new Map<string, string>();
          usersData.forEach(u => {
            const email = (u.email || '').toLowerCase();
            const username = email ? email.split('@')[0] : (u.userId || u._id || 'unknown');
            userNameMap.set(u.userId || (u as any)._id, username);
          });

          scansData = allScans;
          console.log(`[SceneManager] Admin loaded ${scansData.length} total scans`);

          // After transformation, attach ownerUsername
          const transformedScenes = scansData.map(scan => {
            const sceneObj = transformScanToScene(scan);
            const username = userNameMap.get(scan.creatorId) || 'unknown';
            return { ...sceneObj, ownerUsername: username };
          });

          setScans(scansData);
          setScenes(transformedScenes);
          console.log(`[SceneManager] Successfully loaded and transformed ${scansData.length} scans`);
          return; // exit to avoid double-setting later
        case 'creator':
        case 'waitlisted':
        default:
          // Regular users only see their own scans
          scansData = await customerApi.getScans({ creatorId: authUser.uid });
          console.log(`[SceneManager] User loaded ${scansData.length} own scans`);
          break;
      }
      
      const transformedScenes = scansData.map(transformScanToScene);
      
      setScans(scansData);
      setScenes(transformedScenes);
      
      console.log(`[SceneManager] Successfully loaded and transformed ${scansData.length} scans`);
    } catch (error) {
      console.error('[SceneManager] Failed to load user scans:', error);
      setScenesError(error instanceof Error ? error.message : 'Failed to load scans');
      setScenes([]);
      setScans([]);
    } finally {
      setIsLoadingScenes(false);
    }
  }, [authUser, authenticated, role, transformScanToScene]);


const checkReconstructionStatus = useCallback(async (scansToCheck: Scan[]) => {
  if (isCheckingStatus.current) {
    console.log('[SceneManager] Status check already in progress, skipping');
    return;
  }

  isCheckingStatus.current = true;

  try {
    // ✅ SIMPLE: Just find processing scans - no 30-minute checks or complex filtering
    const processingScans = scansToCheck.filter(scan => {
      const isProcessing = scan.status === ScanStatus.Processing;
      if (isProcessing) {
        console.log(`[SceneManager] Found processing scan: ${scan.scanId} (${scan.title})`);
      }
      return isProcessing;
    });

    if (processingScans.length === 0) {
      console.log('[SceneManager] ✅ No processing scans found');
      return;
    }

    console.log(`[SceneManager] 🔍 Using monitoring API for ${processingScans.length} processing scans`);

    // ✅ SIMPLE: Convert scans to the format expected by monitoring API
    const monitoringRequest = {
      scans: processingScans.map(scan => ({
        scanId: scan.scanId,
        status: scan.status,
        sequenceUuid: scan.sequenceUuid,
        title: scan.title,
        creatorId: scan.creatorId,
        activeVersionId: scan.activeVersionId,
        s3FolderPath: scan.s3FolderPath,
        // Include timestamps for proper IScanDocument compatibility
        createdAt: scan.createdAt,
        updatedAt: scan.updatedAt,
        // Add other essential fields needed by monitoring service
        visibility: scan.visibility || 'private',
        activeVersion: scan.activeVersion || 1,
        latestVersion: scan.latestVersion || 1
      }))
    };

    // ✅ SIMPLE: Call the monitoring API - it handles everything (Hive checks, updates, etc.)
    const monitoringResponse = await customerApi.runMonitoringCycle(monitoringRequest);
    
    console.log(`[SceneManager] ✅ Monitoring API completed:`, {
      totalProcessed: monitoringResponse.data.totalProcessed,
      statusUpdated: monitoringResponse.data.statusUpdated,
      errors: monitoringResponse.data.errors.length,
      duration: monitoringResponse.data.duration
    });

    // ✅ SIMPLE: If any scans were updated, refresh to show the latest data
    if (monitoringResponse.data.statusUpdated > 0) {
      console.log(`[SceneManager] 🔄 ${monitoringResponse.data.statusUpdated} scans updated - refreshing data`);
      
      // Refresh scans to get the updated status from backend
      await refreshScans();
      
      // Show success message
      showToast(`Updated ${monitoringResponse.data.statusUpdated} scan(s)`, 'success');
    } else {
      console.log(`[SceneManager] ℹ️ No status changes detected`);
    }

    // ✅ SIMPLE: Log any errors but don't break the flow
    if (monitoringResponse.data.errors.length > 0) {
      console.warn(`[SceneManager] Monitoring errors:`, monitoringResponse.data.errors);
      // Dont show a warning toast
     // showToast(`${monitoringResponse.data.errors.length} scan(s) had errors during monitoring`, 'info');
    }

  } catch (error) {
    console.error(`[SceneManager] Monitoring API failed:`, error);
    //no value of showing this error to customer, we should rather just log it
    //showToast(`Failed to check reconstruction status: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
  } finally {
    isCheckingStatus.current = false;
  }
}, [loadUserScans, showToast]); 


  useEffect(() => {
  // Early exit if we've already run monitoring this session
  if (hasRunReconstructionMonitoring) {
    return;
  }

  // Only check when we first load scans AND we're not loading anymore
  if (scans.length > 0 && !isLoadingScenes && !isCheckingStatus.current) {
    console.log('[SceneManager] First load - checking processing scans');
    checkReconstructionStatus(scans);
    setHasRunReconstructionMonitoring(true);
  }
}, [scans.length, isLoadingScenes, checkReconstructionStatus, hasRunReconstructionMonitoring]);
  
// Load scans when user changes OR when authentication is complete
  useEffect(() => {
    if (authenticated) {
      loadUserScans();
    }
  }, [loadUserScans, authenticated]);



  useEffect(() => {
    console.log('🔍 SceneManager: Location state changed:', location.state);
    console.log('🔍 SceneManager: Current path:', location.pathname);
  
  if (location.state?.selectedScene && scenes.length > 0) {
    const sharedScene = location.state.selectedScene;
    console.log('Auto-selecting shared scene:', sharedScene.name);
    
    // Find or create the scene in our scenes list
    let targetScene = scenes.find(scene => scene.id === sharedScene.id);
    
    if (!targetScene) {
      // If scene not found in user's scenes, add it temporarily
      targetScene = sharedScene;
      setScenes(prev => [...prev, sharedScene]);
    }
    
    // Auto-select the scene (targetScene is guaranteed to exist now)
    if (targetScene) {
      handleSceneSelect(targetScene);
    }
    
    // Clear the navigation state
    window.history.replaceState({}, '');
  }
}, [location.state, scenes, authenticated]);

  // Refresh scans function
  const refreshScans = useCallback(async () => {
  console.log('[SceneManager] Manual refresh - reloading scans and checking status');
  await loadUserScans();
  // Status check will happen automatically via useEffect above
}, [loadUserScans]);

  const handleSceneSelect = async (scene: Scene) => {
  console.log(`[SceneManager] Scene selected: ${scene.name}, status: ${scene.status}`);
  
  // IMMEDIATE VISUAL FEEDBACK - Set loading state right away
  setSceneLoadingStates(prev => ({ ...prev, [scene.id]: true }));
  setLoadingMessage('Preparing scene...');
  
  // Prevent multiple clicks on the same scene
  if (sceneLoadingStates[scene.id]) {
    console.log(`[SceneManager] Scene ${scene.id} already loading, ignoring click`);
    return;
  }
  
  try {
    setSelectedScene(scene);
    
    // Set images for the selected scene
    setImages(scene.images || []);
    
    // Set processing status based on scene status
    setProcessing({
      status: scene.status === 'completed' ? 'completed' : 
              scene.status === 'processing' ? 'processing' : 
              scene.status === 'error' ? 'error' : 'pending',
      progress: scene.status === 'completed' ? 100 : 0,
      message: scene.quality?.feedback?.comment || ''
    });
    
    switch (scene.status) {
      case 'completed':
        console.log(`[SceneManager] Scene is completed, launching 3D viewer...`);
        setLoadingMessage('Loading 3D model...');
        
        const modelData = await getSceneModelUrl(scene);
        
        if (modelData?.modelUrl) {
          console.log(`[SceneManager] 3D model URL found, launching viewer with ${modelData.helperFiles?.length || 0} helper files`);
          setLoadingMessage('Initializing 3D viewer...');
          // Pass helper files to the viewer
          await handleSceneView3D(scene, modelData.modelUrl, modelData.helperFiles);
        } else {
          console.log(`[SceneManager] No 3D model URL found, staying on dashboard`);
          setCurrentView('dashboard');
          setSelectedScene(null);
          showToast('3D model not available for this scene. Please wait for processing to complete.', 'info');
        }
        break;
        
      case 'processing':
        console.log(`[SceneManager] Scene is processing, staying on dashboard`);
        setCurrentView('dashboard');
        setSelectedScene(null);
        showToast('Scene is currently processing. Please wait for it to complete.', 'info');
        break;
        
      case 'error':
        console.log(`[SceneManager] Scene has error, staying on dashboard`);
        setCurrentView('dashboard');
        setSelectedScene(null);
        showToast('Scene processing failed. Please create a new scene to try again.', 'error');
        break;
        
      case 'pending':
        console.log(`[SceneManager] Scene is pending, staying on dashboard`);
        setCurrentView('dashboard');
        setSelectedScene(null);
        showToast('Scene is pending. Please create a new scene to upload images.', 'info');
        break;
        
      case 'uploaded':
        console.log(`[SceneManager] Scene is uploaded, staying on dashboard`);
        setCurrentView('dashboard');
        setSelectedScene(null);
        showToast('Scene is already uploaded. Please wait for processing to complete.', 'info');
        break;
        
      default:
        console.log(`[SceneManager] Unknown scene status: ${scene.status}, staying on dashboard`);
        setCurrentView('dashboard');
        setSelectedScene(null);
        showToast(`Unknown scene status: ${scene.status}. Please contact support if this persists.`, 'error');
        break;
    }
  } catch (error) {
    console.error('[SceneManager] Error handling scene selection:', error);
    showToast(`Error opening scene: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
  } finally {
    // Clear loading state after processing
    setSceneLoadingStates(prev => ({ ...prev, [scene.id]: false }));
    setLoadingMessage('');
  }
};


const handleSceneView3D = async (scene: Scene, modelUrl: string, helperFiles?: any[]) => {
  try {
    console.log(`[SceneManager] Loading 3D viewer for scene: ${scene.name}`);
    console.log(`[SceneManager] Helper files available:`, helperFiles?.length || 0);
    
    // Log helper files details for debugging
    if (helperFiles && helperFiles.length > 0) {
      console.log(`[SceneManager] Helper files details:`, helperFiles.map(f => ({
        fileName: f.fileName,
        fileType: f.fileType,
        signedUrl: f.signedUrl ? f.signedUrl.substring(0, 50) + '...' : 'No URL'
      })));
    }
    
    // STEP 1: Simple cleanup - let React handle DOM
    if (viewerRef.current && viewerRef.current !== 'cleaning') {
      try {
        viewerRef.current.dispose();
      } catch (error) {
        console.warn('Viewer disposal error (continuing):', error);
      }
      viewerRef.current = null;
    }
    
    // STEP 2: Set view state and loading
    setSelectedSceneFor3D(scene); 
    setCurrentView('3d-viewer');
    setModelLoading(true);
    
    // STEP 3: Wait for React to render the container
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // STEP 4: Check container exists
    if (!modelContainerRef.current) {
      throw new Error('3D viewer container not found');
    }
    
    console.log('[SceneManager] Container ready, creating viewer...');
    
    // STEP 5: Create viewer with minimal options
    const viewer = new Viewer({
      sharedMemoryForWorkers: false,
      rootElement: modelContainerRef.current
    });
    viewerRef.current = viewer;
    
    // STEP 6: Load splat scene with helper files 
    console.log('[SceneManager] Adding splat scene...');
    
    // Track highest progress seen to avoid UI regressions
    let lastProgress = 0;
    
    // Prepare scene options with helper files 
    const splatBufferOptions: any = {
      showLoadingUI: false,
      onProgress: (progress: number) => {
        // Ignore any progress values lower than we've already displayed
        if (progress < lastProgress) return;
        lastProgress = progress;

        console.log(`[SceneManager] Loading: ${Math.round(progress)}%`);
        if (progressTextRef.current) {
          progressTextRef.current.textContent = `Loading model... ${Math.round(progress)}%`;
        }
      }
    };

    // Add helper files if available 
    if (helperFiles && helperFiles.length > 0) {
      console.log('[SceneManager] Processing helper files for viewer...');
      
      // Find environment map
      const envFile = helperFiles.find(f => 
        f.fileType === 'env-map' || 
        f.fileName.toLowerCase().includes('env') ||
        f.fileName.toLowerCase().includes('environment') ||
        f.fileName.toLowerCase().includes('hdr')
      );
      
      if (envFile && envFile.signedUrl) {
        console.log('[SceneManager] Found environment map:', envFile.fileName);
        splatBufferOptions.environmentMapPath = envFile.signedUrl;
      }

      // Find poses/camera data  
      const posesFile = helperFiles.find(f => 
        f.fileType === 'poses-json' || 
        f.fileName.toLowerCase().includes('pose') ||
        f.fileName.toLowerCase().includes('camera') ||
        f.fileName.toLowerCase().includes('trajectory')
      );
      
      if (posesFile && posesFile.signedUrl) {
        console.log('[SceneManager] Found poses file:', posesFile.fileName);
        splatBufferOptions.imagePosesPath = posesFile.signedUrl;
      }
    }

    console.log('[SceneManager] Final splatBufferOptions:', splatBufferOptions);
    
    // Load the splat scene with options
    await viewer.addSplatScene(modelUrl, splatBufferOptions);
    
    // STEP 7: Start viewer
    viewer.start();
    
    // STEP 8: Setup keyboard controls for poses (if poses are available)
    if (splatBufferOptions.imagePosesPath) {
      console.log('[SceneManager] Setting up pose visualization controls');
      setupPoseControls(viewer);
    }
    
    // STEP 9: Update state
    setModelLoading(false);
    
    console.log('[SceneManager] 3D viewer loaded successfully');
    
  } catch (error) {
    console.error('[SceneManager] Error loading 3D viewer:', error);
    setModelLoading(false);
    setSelectedSceneFor3D(undefined);
    setCurrentView('dashboard');
    showToast(`Failed to load 3D viewer: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
  }
};

// New function to setup pose controls 
const setupPoseControls = (viewer: any) => {
  console.log('[SceneManager] Setting up pose visualization controls');
  
  // Add keyboard listener for space key
  const handleKeyPress = (event: KeyboardEvent) => {
    if (event.code === 'Space') {
      event.preventDefault();
      console.log('[SceneManager] Space pressed - toggling pose visualization');
      
      try {
        // Toggle camera path/poses visualization
        if (viewer.showImagePoses) {
          viewer.hideImagePoses();
          showToast('Camera poses hidden', 'info');
        } else {
          viewer.showImagePoses();
          showToast('Camera poses shown - Press Space to hide', 'info');
        }
      } catch (error) {
        console.warn('[SceneManager] Error toggling poses:', error);
        // Try alternative methods
        try {
          if (viewer.toggleImagePoses) {
            viewer.toggleImagePoses();
          } else if (viewer.toggleCameraPoses) {
            viewer.toggleCameraPoses();
          } else {
            console.warn('[SceneManager] No pose controls available in this viewer version');
            showToast('Camera pose visualization not available', 'info');
          }
        } catch (fallbackError) {
          console.warn('[SceneManager] Fallback pose controls failed:', fallbackError);
        }
      }
    }
  };
  
  // Add event listener
  window.addEventListener('keydown', handleKeyPress);
  
  // Store the handler for cleanup
  (viewer as any)._poseControlHandler = handleKeyPress;
  
  // Show initial instruction
  showToast('Press SPACE to show/hide camera poses', 'info');
};

// SAFE VERSION: Much simpler cleanup
const cleanupViewer = () => {
  console.log('Cleaning up viewer');
  
  if (viewerRef.current && viewerRef.current !== 'cleaning') {
    try {
      // Mark as cleaning to prevent double disposal
      const viewer = viewerRef.current;
      viewerRef.current = 'cleaning' as any;
      
      // Remove pose control event listener if it exists
      if ((viewer as any)._poseControlHandler) {
        window.removeEventListener('keydown', (viewer as any)._poseControlHandler);
        delete (viewer as any)._poseControlHandler;
        console.log('[SceneManager] Removed pose control handler');
      }
      
      if (typeof viewer.dispose === 'function') {
        viewer.dispose();
      }
      console.log('Viewer disposed successfully');
    } catch (error) {
      console.warn('Viewer disposal error (ignoring):', error);
      // Don't throw - just log and continue
    }
    
    // Always reset to null after cleanup attempt
    viewerRef.current = null;
  }
  
  // Clear progress text
  if (progressTextRef.current) {
    progressTextRef.current.textContent = '';
  }
};

// Update the close3DViewer function to be async and handle errors:
const close3DViewer = async () => {
  console.log('Closing 3D viewer');
  
  // Prevent multiple simultaneous close operations
  if (viewerRef.current === 'cleaning') {
    console.log('Close already in progress, skipping...');
    return;
  }
  
  try {
    cleanupViewer();
    
    // Update state after cleanup
    setSelectedSceneFor3D(undefined);
    setModelLoading(false);
    setCurrentView('dashboard');
    setSelectedScene(null);
    
    console.log('3D viewer closed successfully');
  } catch (error) {
    console.warn('Error closing 3D viewer:', error);
    // Still update state even if cleanup failed
    setSelectedSceneFor3D(undefined);
    setModelLoading(false);
    setCurrentView('dashboard');
    setSelectedScene(null);
  }
};

// FIXED: Better escape key handler with error prevention:
useEffect(() => {
  const handleKeyDown = async (event: KeyboardEvent) => {
    if (event.key === 'Escape' && currentView === '3d-viewer') {
      event.preventDefault();
      event.stopPropagation();
      
      try {
        await close3DViewer();
      } catch (error) {
        console.warn('Error handling escape key:', error);
        // Force close by updating state directly
        setCurrentView('dashboard');
        setSelectedScene(null);
      }
    }
  };

  if (currentView === '3d-viewer') {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }
}, [currentView]);

// OPTIONAL: Add a component unmount cleanup (insurance policy):
useEffect(() => {
  return () => {
    // Component unmount cleanup - be very defensive
    if (viewerRef.current && viewerRef.current !== 'cleaning') {
      try {
        if (typeof viewerRef.current.dispose === 'function') {
          viewerRef.current.dispose();
        }
      } catch (error) {
        // Silently ignore errors during unmount
        console.warn('Unmount cleanup error (ignoring):', error);
      } finally {
        viewerRef.current = null;
      }
    }
  };
}, []); // Empty dependency array - only runs on unmount

//  Refreshes scans when returning from editor:
const handleBack = () => {
  if (currentView === '3d-viewer') {
    close3DViewer();
  } else {
    // Store the previous view before changing
    const wasInEditor = currentView === 'editor';
    
    setCurrentView('dashboard');
    setSelectedScene(null);
    setImages([]);
    setSelectedImage(null);
    setProcessing({ status: 'pending', progress: 0 });
    resetUuid();
    
    // If returning from editor, refresh scans to show updated status
    if (wasInEditor) {
      console.log('📋 Returned from editor - refreshing scans...');
      refreshScans();
    }
  }
};

  // Handle scene creation
  const handleCreateScene = (sceneData: Omit<Scene, 'id' | 'images' | 'quality' | 'thumbnail'>) => {
    const newScene: Scene = {
      id: Math.random().toString(36).substring(7),
      ...sceneData,
      images: [],
      versions: [],
      quality: {
        overall: 0,
        imageCount: 0
      }
    };
    setScenes(prev => [...prev, newScene]);
    setSelectedScene(newScene);
    setImages([]);
    setSelectedImage(null);
    setProcessing({ status: 'pending', progress: 0 });
    setCurrentView('editor');
  };

  // Handle sharing
  const handleShare = (scene: Scene) => {
    setSceneToShare(scene);
    setShareModalOpen(true);
  };
  
  const handleShareSuccess = (shareData: any) => {
    console.log('[SceneManager] Scene shared successfully:', shareData);
    showToast('Scene shared successfully! An invitation has been sent.', 'success');
  };
  
  const handleShareClose = () => {
    setShareModalOpen(false);
    setSceneToShare(null);
  };

  // Handle download (placeholder)
  const handleDownload = (scene: Scene) => {
    console.log('[SceneManager] Opening download modal for:', scene.name);
    setSceneToDownload(scene);
    setDownloadModalOpen(true);
  };
  
  // Handle download modal close
  const handleDownloadModalClose = () => {
    setDownloadModalOpen(false);
    setSceneToDownload(null);
  };

  // Handle delete (soft-delete via backend)
  const handleDelete = async (scene: Scene) => {
    try {
      // 1️⃣  Update Scan status to "deleted" (soft-delete)
      await customerApi.updateScanStatus(scene.id, ScanStatus.Deleted);

      // 2️⃣  Also mark the active version as deleted (best-effort)
      try {
        const scanDetails = await customerApi.getScanById(scene.id);
        const activeVersionId = scanDetails.activeVersionId;
        if (activeVersionId) {
          await customerApi.updateScanVersion(activeVersionId, { status: ScanStatus.Deleted });
        }
      } catch (verErr) {
        console.warn(`[SceneManager] Failed to update active version status for ${scene.id}:`, verErr);
      }

      // 3️⃣  Remove locally so UI updates immediately
      setScenes(prevScenes => prevScenes.filter(s => s.id !== scene.id));

      // Reset editor/selection state if needed
      if (selectedScene?.id === scene.id) {
        setSelectedScene(null);
        setImages([]);
        setSelectedImage(null);
        setProcessing({ status: 'pending', progress: 0 });
        setCurrentView('dashboard');
      }

      showToast(`Scene "${scene.name}" deleted successfully`, 'success');

      // 🔄  Refresh scans list so all tabs reflect the change
      refreshScans();
    } catch (error) {
      console.error('[SceneManager] Failed to delete scan:', error);
      showToast('Failed to delete scene. Please try again.', 'error');
    }
    showToast(`Scene "${scene.name}" deleted successfully`, 'success');
  };

  // Handle rate/feedback
  const handleRate = () => {
    setShowFeedback(true);
  };

  const handleSubmitFeedback = (feedback: any) => {
    if (selectedScene) {
      setScenes((prevScenes: Scene[]) =>
        prevScenes.map((scene) => {
          if (scene.id === selectedScene.id) {
            return {
              ...scene,
              quality: {
                ...scene.quality,
                feedback: {
                  ...feedback,
                  submittedAt: Date.now(),
                },
              },
            };
          }
          return scene;
        })
      );
      showToast('Feedback submitted successfully!', 'success');
    }
    setShowFeedback(false);
  };

  // Render based on current view
  const renderContent = () => {
    switch (currentView) {
      case 'dashboard':
        return (
          <Dashboard
            showNav={false}
            scenes={scenes}
            isLoadingScenes={isLoadingScenes}
            scenesError={scenesError}
            onRefreshScans={refreshScans}
            onSceneSelect={handleSceneSelect}
            onCreateScene={handleCreateScene}
            onShare={handleShare}
            onDownload={handleDownload}
            onDelete={handleDelete}
            onRate={handleRate}
            showFeedback={showFeedback}
            setShowFeedback={setShowFeedback}
            selectedScene={selectedScene}
            user={user}
            onLogout={onLogout}
            onSubmitFeedback={handleSubmitFeedback}
            sceneLoadingStates={sceneLoadingStates}
            loadingMessage={loadingMessage}
          />
        );

      case 'editor':
        return selectedScene ? (
          <SceneEditor
            scene={selectedScene}
            onBack={handleBack}
            onReconstruct={handleReconstruction}
            images={images}
            selectedImage={selectedImage}
            processing={processing}
            onImagesSelected={handleImagesSelected}
            onImageClick={handleImageClick}
            onImageDelete={handleImageDelete}
          />
        ) : null;

        

      case '3d-viewer':
        return (
          <div className="fixed inset-0 w-full h-full bg-black z-[999]">
            {/* Close button for 3D viewer */}
            <div className="fixed top-4 right-4 z-[1001]">
              <button
                onClick={close3DViewer}
                className="bg-[#161513]/90 backdrop-blur-sm text-white px-6 py-3 shadow-lg hover:bg-[#161513] transition-all duration-200 flex items-center gap-3 font-medium border border-[#0CB4E4]/30 rounded-xl"
              >
                <span className="text-lg">✕</span>
                <span>Exit</span>
                <span className="text-xs text-gray-400">(ESC)</span>
              </button>
            </div>

            {/* Logo and brand overlay */}
            {selectedSceneFor3D && !modelLoading && (
              <div 
                className="fixed top-4 left-4 z-[1001] flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => {
                  close3DViewer();
                  // Navigate back to dashboard or home
                }}
                title="Back to dashboard"
              >
                <img 
                  src={logo} 
                  alt="CR3ATE.AI" 
                  className="h-10 w-10 object-contain" 
                />
                <h1 className="font-light text-xl text-[#0CB4E4] font-brand">CR3ATE.AI</h1>
              </div>
            )}

            {selectedSceneFor3D && !modelLoading && (
              <SceneDetailsPanel 
                model={{
                  fileName: selectedSceneFor3D.name,
                  description: selectedSceneFor3D.description,
                  fileType: 'gaus',
                  fileSize: sceneFileSizes[selectedSceneFor3D.id], 
                  captureType: selectedSceneFor3D.captureType || 'unknown',
                  cameraType: selectedSceneFor3D.cameraType, //needs from scan metadata
                  sceneType: selectedSceneFor3D.type, //needs from scan metadata
                  category: 'Reconstruction',
                  createdAt: selectedSceneFor3D.createdAt
                }}
                onClose={close3DViewer}
                {...(['creator','admin'].includes(role ?? '') && selectedSceneFor3D.creatorId === authUser?.uid ? { onShare: () => handleShare(selectedSceneFor3D) } : {})}
                onDownload={() => handleDownload(selectedSceneFor3D)}
                // onDelete={() => handleDelete(selectedSceneFor3D)} // Optional: enable if you want delete from 3D viewer
              />
            )}

            {/* Model loading spinner */}
            {modelLoading && (
              <div className="fixed inset-0 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm z-[1002]">
                <div className="animate-spin h-16 w-16 border-4 border-[#0CB4E4] border-t-transparent mb-6"></div>
                <p ref={progressTextRef} className="text-white text-lg font-medium">
                  Loading 3D model...
                </p>
                <p className="text-white/70 text-sm mt-2">
                  Please wait while we load your reconstruction
                </p>
              </div>
            )}

            {/* 3D Model Viewer Container */}
            <div
              ref={modelContainerRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                zIndex: selectedSceneFor3D ? 1000 : -1,
                pointerEvents: selectedSceneFor3D ? "auto" : "none",
                backgroundColor: "black"
              }}
            />
          </div>
        );    
      default:
        return null;
    }
  };

  return (
    <>
      {renderContent()}
      
      {/* Share Modal */}
      {sceneToShare && (
        <ShareModal
          scene={sceneToShare}
          isOpen={shareModalOpen}
          onClose={handleShareClose}
          onSuccess={handleShareSuccess}
        />
      )}
      
      {/* Feedback Modal */}
      {showFeedback && selectedScene && (
        <ReconstructionFeedback
          onSubmit={handleSubmitFeedback}
          onClose={() => setShowFeedback(false)}
        />
      )}
      
      {/* Toast Notifications */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={handleToastClose}
      />
      
      {/* Download Modal */}
      {sceneToDownload && (
        <DownloadModal
          scene={sceneToDownload}
          isOpen={downloadModalOpen}
          onClose={handleDownloadModalClose}
        />
      )}
    </>
  );
}
